import React from 'react';
import { Anchor, Box, Button, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconDeviceMobile, IconMailFast } from '@tabler/icons-react';
import FlowAlert from '@/components/auth/flow/components/parts/Flow.Alert';
import FlowAlternatives from '@/components/auth/flow/components/parts/Flow.Alternatives';
import FlowCodeField from '@/components/auth/flow/components/parts/Flow.Code.Field';
import { AUTH_CODE_LENGTH } from '@/function/auth/code.sanitize';
import flowPurposeOf from '@/function/auth/flow.purpose.of';
import type { TypeFlowScreenProps } from '@/types/auth/types.auth.flow';

type Props = TypeFlowScreenProps & {
    step: string;
    mode: 'email' | 'totp';
    destination: string | null;
    passkeySupported: boolean;
};

const FlowCode: React.FC<Props> = ({ flow, message, step, mode, destination, passkeySupported }) => {
    const [code, setCode] = React.useState('');
    const [local, setLocal] = React.useState<string | null>(null);
    const fieldError = local ?? (message?.field === 'code' ? message.text : null);

    React.useEffect(() => {
        if (message?.field === 'code') setCode('');
    }, [message]);

    const send = (value: string) => {
        if (flow.busy) return;
        if (value.length !== AUTH_CODE_LENGTH) {
            setLocal(`Il codice ha ${AUTH_CODE_LENGTH} cifre`);
            return;
        }
        void flow.submit(step, { code: value });
    };

    const sender = flow.state.phase === 'step'
        ? flow.state.allowed.find((item) => item.step === 'email-otp.send' || item.step === 'password-recovery.request')
        : undefined;

    const resend = () => {
        if (flow.busy || !sender) return;
        setCode('');
        setLocal(null);
        void flow.submit(sender.step, sender.step === 'email-otp.send' ? { purpose: flowPurposeOf(flow.kind, sender) } : {});
    };

    return (
        <form onSubmit={(event) => { event.preventDefault(); send(code); }} noValidate>
            <Stack align="center" gap="lg">
                <ThemeIcon size={64} radius="xl" variant="light" color="blue">
                    {mode === 'email' ? <IconMailFast size={34} /> : <IconDeviceMobile size={34} />}
                </ThemeIcon>
                <Box style={{ textAlign: 'center', maxWidth: 380 }}>
                    <Title order={3}>{mode === 'email' ? 'Inserisci il codice' : 'Codice dell’app'}</Title>
                    <Text c="dimmed" size="sm" mt={6} style={{ overflowWrap: 'anywhere' }}>
                        {mode === 'email'
                            ? `Abbiamo inviato un codice a 6 cifre${destination ? ` a ${destination}` : ' alla tua email'}. Vale pochi minuti.`
                            : 'Apri l’app di autenticazione e inserisci il codice a 6 cifre.'}
                    </Text>
                </Box>
                <Box w="100%"><FlowAlert message={message} field="code" /></Box>
                <FlowCodeField
                    label={mode === 'email' ? 'Codice ricevuto via email' : 'Codice dell’app'}
                    value={code}
                    error={fieldError}
                    disabled={flow.busy}
                    onChange={(value) => { setLocal(null); setCode(value); }}
                    onComplete={send}
                />
                <Stack gap={8} w="100%" maw={320}>
                    <Button type="submit" fullWidth radius="md" loading={flow.busy} disabled={flow.busy || code.length !== AUTH_CODE_LENGTH}>CONFERMA</Button>
                    {mode === 'email' && sender ? (
                        <Anchor component="button" type="button" size="sm" ta="center" disabled={flow.busy} style={{ minHeight: 44 }} onClick={resend}>Invia di nuovo il codice</Anchor>
                    ) : null}
                </Stack>
                <FlowAlternatives flow={flow} current={mode === 'email' ? 'email-code' : 'totp'} passkeySupported={passkeySupported} />
            </Stack>
        </form>
    );
};

export default FlowCode;
